import { error } from '@sveltejs/kit';
import { page } from '$app/state';
import { thumbnailUrl, truncate } from '$lib';
import { SvelteMap } from 'svelte/reactivity';

import type { PageLoadEvent } from './$types';

import ProductPage from './ProductPage.svelte';

export type ProductPageData = {
	product: Product;
	selectedVariant: Variant | undefined;
	selectedOptions: SvelteMap<ProductOption | ProductSubOption, ProductOptionValue | string>;
	jsonLd?: object;
};
export const productLoad = async ({ params, fetch, parent, url }: PageLoadEvent) => {
	const { store, title, metaDescription } = await parent();

	const urlParams = new URLSearchParams({
		slug: params.slug,
	});
	const response = await fetch(`/api/content/product?${urlParams}`, {
		headers: {
			'X-Store-ID': store.store_id.toString(),
		},
	});
	if (response.status === 404) error(404);
	if (response.status !== 200) error(500);

	const product = (await response.json()) as Product;

	const productMetaDescription = truncate(
		product.data.short_description ?? product.data.description,
		200,
	);

	return {
		component: ProductPage,
		pageData: {
			product: product,
			selectedVariant: getSelectedVariant(product, url),
			selectedOptions: new SvelteMap(),
			jsonLd: generateJsonLd(product, store.currency, url, store.media_base_url),
		} satisfies ProductPageData,
		title: product.data.name || title,
		metaDescription: productMetaDescription || metaDescription,
	};
};

function getSelectedVariant(product: Product, url: URL): Variant | undefined {
	const variantParam = url.searchParams.get(`variant[131]`);

	if (variantParam) {
		return product.variants.find((v) => v.variation_id === Number(variantParam));
	}

	return product.variants.length === 1 ? product.variants[0] : undefined;
}

function generateJsonLd(
	product: Product,
	currency: string,
	url: URL | string,
	media_base_url: string,
) {
	if (product.variants.length === 0 || product.data.images.length === 0) {
		return;
	}

	const LIMIT = 5_000;

	const descriptionTruncated = truncate(product.data.description, LIMIT);

	return {
		'@context': 'https://schema.org',
		'@type': 'Product',
		brand: {
			'@type': 'Brand',
			name: product.data.brand.name,
		},
		sku: product.data.sku,
		name: product.data.name,
		color: product.data.color,
		description: ['', '...'].includes(descriptionTruncated)
			? product.data.name
			: descriptionTruncated,
		itemCondition: 'https://schema.org/NewCondition',
		image: product.data.images.map((img) => thumbnailUrl(media_base_url, `product/${img}`, 1800)),
		offers: createOffers(product, currency, url),
	};
}

function createOffers(product: Product, currency: string, url: URL | string) {
	if (product.variants.length == 1) {
		return createOffer(product.variants[0], currency, url);
	}

	return product.variants.map((variant) => createOffer(variant, currency, url));
}

function createOffer(variant: Variant, currency: string, url: URL | string) {
	return {
		'@type': 'Offer',
		availability: variant.is_sold_out
			? 'https://schema.org/OutOfStock'
			: 'https://schema.org/InStock',
		price: variant.special_price ?? variant.price,
		priceCurrency: currency,
		sku: variant.data.sku,
		mpn: variant.data.mpn,
		gtin: variant.data.ean_code,
		url,
	};
}

export async function fetchComplementary(urlParams: URLSearchParams, fetch = window.fetch) {
	const response = await fetch(`/api/content/recommend/complementary?${urlParams}`, {
		headers: {
			'X-Store-ID': page.data.store.store_id.toString(),
		},
	});

	if (response.status !== 200) {
		return undefined;
	}

	return (await response.json()) as ProductFromCategory[];
}

export async function fetchBestSellers(urlParams: URLSearchParams, fetch = window.fetch) {
	const response = await fetch(`/api/content/recommend/best-sellers?${urlParams}`, {
		headers: {
			'X-Store-ID': page.data.store.store_id.toString(),
		},
	});

	if (response.status !== 200) {
		return undefined;
	}

	return (await response.json()) as ProductFromCategory[];
}

export { createOffer, generateJsonLd };
